import { useState } from "react";
import { derivePregnancyDates } from "@/lib/dates";
import type { OnboardingDraft } from "./OnboardingFlow";

type DateInputMode = "lmp" | "due" | "conception";

export default function Step2Pregnancy({
  draft,
  onNext,
  onBack,
}: {
  draft: OnboardingDraft;
  onNext: (patch: Partial<OnboardingDraft>) => void;
  onBack: () => void;
}) {
  const [mode, setMode] = useState<DateInputMode>("lmp");
  const [dateValue, setDateValue] = useState("");
  const [isMultiples, setIsMultiples] = useState(draft.isMultiples ?? false);
  const [fetusCount, setFetusCount] = useState(draft.fetusCount ?? 2);
  const [hasGD, setHasGD] = useState(draft.hasGestationalDiabetes ?? false);
  const [hasHTN, setHasHTN] = useState(draft.hasHypertension ?? false);
  const [currentWeight, setCurrentWeight] = useState(
    draft.currentWeightKg ? String(draft.currentWeightKg) : ""
  );

  const canContinue = dateValue && currentWeight;

  function handleContinue() {
    const dates = derivePregnancyDates({ [mode === "lmp" ? "lmpDate" : mode === "due" ? "dueDate" : "conceptionDate"]: dateValue } as never);
    onNext({
      lmpDate: dates.lmpDate,
      dueDate: dates.dueDate,
      conceptionDate: dates.conceptionDate,
      isMultiples,
      fetusCount: isMultiples ? fetusCount : 1,
      hasGestationalDiabetes: hasGD,
      hasHypertension: hasHTN,
      currentWeightKg: Number(currentWeight),
    });
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-semibold text-forest-700">About your pregnancy</h1>
        <p className="mt-1 text-sm text-forest-500">
          Give us whichever date you know — we'll work out the rest.
        </p>
      </div>

      <div>
        <span className="mb-2 block text-sm font-medium text-ink">I know my...</span>
        <div className="grid grid-cols-3 gap-2">
          {(
            [
              ["lmp", "Last period"],
              ["due", "Due date"],
              ["conception", "Conception"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setMode(value)}
              className={`chip ${mode === value ? "chip-selected" : ""}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <input
        className="input"
        type="date"
        value={dateValue}
        onChange={(e) => setDateValue(e.target.value)}
      />

      <div className="flex items-center justify-between rounded-card border border-line px-4 py-3">
        <div>
          <p className="text-sm font-medium text-ink">Expecting multiples?</p>
          <p className="text-xs text-forest-500">Twins, triplets, etc.</p>
        </div>
        <Toggle checked={isMultiples} onChange={setIsMultiples} />
      </div>

      {isMultiples && (
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-ink">How many?</span>
          <input
            className="input"
            type="number"
            min={2}
            max={5}
            value={fetusCount}
            onChange={(e) => setFetusCount(Number(e.target.value))}
          />
        </label>
      )}

      <label className="block">
        <span className="mb-1 block text-sm font-medium text-ink">Current weight (kg)</span>
        <input
          className="input"
          type="number"
          inputMode="decimal"
          value={currentWeight}
          onChange={(e) => setCurrentWeight(e.target.value)}
          placeholder="For your weekly weight trend"
        />
      </label>

      <div className="rounded-card border border-line px-4 py-3">
        <p className="mb-2 text-sm font-medium text-ink">Optional — only if it applies</p>
        <CheckboxRow
          label="Gestational diabetes"
          checked={hasGD}
          onChange={setHasGD}
        />
        <CheckboxRow label="Hypertension" checked={hasHTN} onChange={setHasHTN} />
      </div>

      <div className="mt-2 flex gap-3">
        <button className="btn-secondary" onClick={onBack}>
          Back
        </button>
        <button className="btn-primary" disabled={!canContinue} onClick={handleContinue}>
          Continue
        </button>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`h-8 w-14 shrink-0 rounded-pill p-1 transition-colors ${
        checked ? "bg-forest-700" : "bg-line"
      }`}
    >
      <span
        className={`block h-6 w-6 rounded-full bg-white transition-transform ${
          checked ? "translate-x-6" : ""
        }`}
      />
    </button>
  );
}

function CheckboxRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex h-tap items-center gap-3 text-sm text-ink">
      <input
        type="checkbox"
        className="h-5 w-5 accent-forest-700"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}
